import { Directive, ElementRef, Input, OnDestroy, OnInit } from '@angular/core';
import { Client } from '@df/core/service/client';
import { Renderer } from '@df/ng/renderer';
import compact from 'lodash-es/compact';
import difference from 'lodash-es/difference';
import forEach from 'lodash-es/forEach';
import union from 'lodash-es/union';
import uniq from 'lodash-es/uniq';
import { Subscription } from 'rxjs';

/**
 * @interface IInvertedClassMap
 */
interface IInvertedClassMap {
    [className: string]: string[];
}

/**
 * Simplified syntax for size-dependand classes.
 *
 * ```html
 * sizeClass="S:m-l-2 m-r-2, XL:m-t-12,X:m-b-4"
 * ```
 */
@Directive({
    selector: '[sizeClass]',
    standalone: true
})
export class SizeClassDirective implements OnInit, OnDestroy {
    /**
     * Map with classes
     * className => sizeIds[]
     */
    private invertedClassMap: IInvertedClassMap = {};

    /**
     * Subscription removal
     * @type {Function}
     */
    private subscription: Subscription = new Subscription();

    constructor(
        private ngEl: ElementRef,
        private renderer: Renderer,
        private client: Client
    ) {}

    /**
     * On init hook subscribes directive to viewport changes
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     */
    ngOnInit() {
        this.subscription.add(this.client.obs.breakpoint.subscribe(() => this.update()));
    }

    /**
     * Unsubscribe on destroy
     * @author Maurycy Ornat maurycy@tomandco.co.uk
     */
    ngOnDestroy() {
        this.subscription.unsubscribe();
    }

    /**
     * Size class input
     * whenever changed reparses and applies changes
     */
    @Input()
    set sizeClass(sizeClass: string) {
        const invertedClassMap: IInvertedClassMap = {};
        if (sizeClass) {
            // first we will create simple map
            // example:
            // size-class='S:m-l-2 m-r-2, XL:m-t-12;X:m-b-4'
            // will be parset to
            // map = { S:['m-l-2','m-r-2'], L : ['m-t-12'], X: ['m-t-12','m-b-4']}'
            const map: Record<string, string[]> = {};
            const ALL: string[] = ['S', 'M', 'L', 'X'];

            sizeClass.split(',').forEach(rule => {
                if (rule) {
                    const parts = rule.split(':', 2);
                    const sizeString = parts[0].toUpperCase().replace(' ', '').replace('A', ALL.join());
                    const sizeIds = sizeString[0] === '!' ? difference(ALL, sizeString.substr(1).split('')) : sizeString.split('');
                    if (parts[1]) {
                        const classes = compact(parts[1].split(' '));
                        sizeIds.forEach(sizeId => {
                            map[sizeId] = union(classes, map[sizeId]);
                        });
                    }
                }
            });

            // not lets invert it
            // so we will have
            // m-l-2 : [S], m-r-2 : [S], m-t-12 : [L,X], m-b-4 : [X]
            forEach(map, (classes, sizeId) => {
                forEach(uniq(classes), (className: string) => {
                    if (className) {
                        if (!invertedClassMap[className]) {
                            invertedClassMap[className] = [];
                        }
                        (<string[]>invertedClassMap[className]).push(sizeId);
                    }
                });
            });
        }

        this.invertedClassMap = invertedClassMap;
        this.update();
    }

    update() {
        const size = this.client.size;

        forEach(this.invertedClassMap, (sizes, className) => {
            this.renderer.setClass(this.ngEl.nativeElement, className, sizes.includes(size));
        });
    }
}
